import { capitalize, lowerCase } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { ageFromDateIso, ValidDateFormats } from './DateUtilities';
import { Reference } from './SharedModels';
import { DateIso, Nullable, NullableString } from './Types';

export function insertSubString(original: string, position: number, value: string): string {
    return [original.slice(0, position), value, original.slice(position)].join('');
}

export function formatNumber(num: number | string): string {
    return num.toLocaleString();
}

export function currencyFormat(num: number | string): string {
    return num.toLocaleString('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });
}

export function sentenceCase(value: string): string {
    return capitalize(lowerCase(value));
}

export function caseInsensitiveStringSort(a: string, b: string) {
    return a.toLowerCase().localeCompare(b.toLowerCase());
}

export function nameFirstLast(first: NullableString, last: NullableString): string {
    return [first, last].filter(n => !!n).join(' ');
}

export function nameTitleFirstLast(title: NullableString, first: NullableString, last: NullableString): string {
    return [title, first, last].filter(n => !!n).join(' ');
}

export function nameLastFirstTitle(last: NullableString, first: NullableString, title: NullableString): string {
    const lastName = last || '';
    let name = [lastName.toUpperCase(), first].filter(n => !!n).join(', ');
    if (title) name += ` (${capitalize(title)})`;
    return name;
}

export function toTitleCase(str: string): string {
    if (!str) return str;
    return str.replace(/([^\W_]+[^\s-]*) */g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

export function trimRight(str: string, charToBeTrimmed: string): string {
    if (!str || !charToBeTrimmed) return str;

    const regexp = new RegExp(`\\${charToBeTrimmed}*$`);
    return str.replace(regexp, '');
}

export function capitalizeFirstLetter(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

export function toLowerCaseFirstLetter(value: string): string {
    return value.charAt(0).toLowerCase() + value.slice(1);
}

export function standardName(nameParts: {
    givenNames: NullableString;
    familyName: NullableString;
    middleNames?: NullableString;
    title?: NullableString;
}) {
    const { givenNames, familyName, middleNames, title } = nameParts;
    const formatTitle = (str?: NullableString): string => {
        let formatted = str || '';
        formatted = trimRight(formatted, '.');
        formatted = toTitleCase(formatted);
        formatted = !!formatted ? `(${formatted})` : formatted;
        return formatted;
    };

    const formattedTitle = formatTitle(title || '');
    const formattedMiddleNames = toTitleCase(middleNames || '');
    const formattedGivenNames = toTitleCase(givenNames || '');
    const formattedFamilyName = (familyName || '').toUpperCase();
    const formattedName = [formattedFamilyName, formattedGivenNames].filter(x => !!x).join(', ');
    return [formattedName, formattedMiddleNames, formattedTitle].filter(x => !!x).join(' ');
}

export function createPatientInfoLine(patient: {
    givenNames?: string;
    middleNames?: string;
    familyName?: string;
    sex?: Nullable<Reference>;
    dateOfBirth?: DateIso;
}): React.ReactNode {
    const patientName = standardName({
        familyName: patient.familyName || '',
        givenNames: patient.givenNames || '',
        middleNames: patient.middleNames || ''
    });

    const patientDateOfBirth = !!patient.dateOfBirth
        ? moment(patient.dateOfBirth, ValidDateFormats.IsoFormat).format(ValidDateFormats.Display)
        : '';

    const patientAge = !!patient.dateOfBirth ? ageFromDateIso(patient.dateOfBirth) : '';

    const dobAgeGenderInfo = [patientDateOfBirth, patientAge, !!patient.sex ? capitalize(patient.sex.name) : '']
        .filter(it => !!it)
        .join(' | ');

    return [patientName, dobAgeGenderInfo].filter(it => !!it).join(', ');
}

type CurrencyFormatOptions = {
    locale: string;
    currency: string;
    displayWhenEmpty: string;
    displayWhenInvalid: string;
    displayWhenZero: string;
};

export function displayCurrency(amount: string | number, opts?: Partial<CurrencyFormatOptions>) {
    const myOpts: CurrencyFormatOptions = {
        locale: (opts && opts.locale) || 'en-AU',
        currency: (opts && opts.currency) || 'AUD',
        displayWhenEmpty: (opts && opts.displayWhenEmpty) || '-',
        displayWhenInvalid: (opts && opts.displayWhenInvalid) || '-',
        displayWhenZero: (opts && opts.displayWhenZero) || '$0.00'
    };

    if (typeof amount === 'number') {
        return formatNumberAsCurrency(amount, myOpts);
    }

    return formatStringAsCurrency(amount, myOpts);
}

function formatNumberAsCurrency(amount: number, opts: CurrencyFormatOptions) {
    if (Number.isNaN(amount)) return opts.displayWhenInvalid;

    if (amount === 0) return opts.displayWhenZero;

    const { locale, currency } = opts;

    return amount.toLocaleString(locale, { style: 'currency', currency });
}

function formatStringAsCurrency(amount: string, opts: CurrencyFormatOptions): string {
    if (!amount || amount.trim() === '') return opts.displayWhenEmpty;

    const cleaned = amount.replace(/[^0-9\.]/g, '');
    if (cleaned === '') return opts.displayWhenInvalid;

    const asNumber = Number(cleaned);

    return formatNumberAsCurrency(asNumber, opts);
}
